import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CheckSquare, Clock, AlertTriangle, Users, Play, Square, User, Calendar, ChevronRight, ChevronDown, X, Send, Lightbulb, TrendingUp, TrendingDown, Target, Download } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { DailyTipCard } from '../components/DailyTipCard';
import { Switch } from '../components/ui/switch';
import { LogClientTimeButton } from '../components/LogClientTimeButton';
import { CelebrationsBanner } from '../components/CelebrationsBanner';
import { MyProfileDatesDialog } from '../components/MyProfileDatesDialog';
import { UpcomingCelebrationsCard } from '../components/UpcomingCelebrationsCard';
import { MyDaySummaryCard } from '../components/MyDaySummaryCard';
import { CrossTeamTasksCard } from '../components/CrossTeamTasksCard';
import { Cake } from 'lucide-react';
import { STATUS_COLORS, STATUS_LABELS_FULL as STATUS_LABELS } from '../utils/constants';

function TeamPerformanceChart() {
  const [data, setData] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = period !== 'all' ? `?period=${period}` : '';
        const res = await api.get(`/analytics/team-performance${params}`);
        setData(res.data);
      } catch (err) { console.warn('Team performance fetch failed:', err); }
    };
    fetch();
  }, [period]);

  if (!data || !data.teams || data.teams.length === 0) return null;

  const RateBar = ({ value, color, bgColor }) => (
    <div className="flex items-center gap-2 w-full">
      <div className={`h-2 rounded-full flex-1 ${bgColor}`}>
        <div className={`h-2 rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 min-w-[40px] text-right">{value}%</span>
    </div>
  );

  const MetricCell = ({ label, value, icon: Icon, color, bgColor, barColor, barBg }) => (
    <div className="flex-1">
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className={`h-5 w-5 rounded ${bgColor} flex items-center justify-center`}>
          <Icon className={`h-3 w-3 ${color}`} />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</span>
      </div>
      <RateBar value={value} color={barColor} bgColor={barBg} />
    </div>
  );

  return (
    <Card className="border-slate-200 shadow-sm mb-6" data-testid="team-performance-chart">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5 text-[#5B6FD6]" /> Team Performance Tracking
          </CardTitle>
          <div className="flex items-center gap-1" data-testid="perf-period-filter">
            {[
              { key: 'all', label: 'All Time' },
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'this_quarter', label: 'This Quarter' },
            ].map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${period === p.key ? 'bg-[#5B6FD6] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`perf-period-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-100">
          {data.teams.map(team => {
            const isExpanded = expandedTeam === team.admin_id;
            return (
              <div key={team.admin_id}>
                <div
                  className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50/80 transition-colors"
                  onClick={() => setExpandedTeam(isExpanded ? null : team.admin_id)}
                  data-testid={`team-row-${team.admin_id}`}
                >
                  <div className="w-40 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <span className="font-semibold text-sm text-slate-900">{team.admin_name}</span>
                    </div>
                    <span className="text-[11px] text-slate-400 ml-6">{team.member_count} members · {team.total} tasks</span>
                  </div>
                  <MetricCell label="Completion" value={team.completion_rate} icon={TrendingUp} color="text-emerald-600" bgColor="bg-emerald-50" barColor="bg-emerald-500" barBg="bg-emerald-100" />
                  <MetricCell label="Delay Rate" value={team.delay_rate} icon={TrendingDown} color="text-red-600" bgColor="bg-red-50" barColor="bg-red-500" barBg="bg-red-100" />
                  <MetricCell label="On-Time" value={team.on_time_rate} icon={Target} color="text-[#5B6FD6]" bgColor="bg-[#5B6FD6]/10" barColor="bg-[#5B6FD6]" barBg="bg-[#5B6FD6]/10" />
                </div>

                {isExpanded && (
                  <div className="bg-slate-50/60 border-t border-slate-100" data-testid={`team-members-${team.admin_id}`}>
                    <div className="grid grid-cols-12 gap-2 px-5 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      <div className="col-span-3 pl-8">Member</div>
                      <div className="col-span-1 text-center">Tasks</div>
                      <div className="col-span-1 text-center">Done</div>
                      <div className="col-span-2 text-center">Completion</div>
                      <div className="col-span-2 text-center">Delay Rate</div>
                      <div className="col-span-2 text-center">On-Time</div>
                    </div>
                    {team.members.map(m => (
                      <div key={m.id} className="grid grid-cols-12 gap-2 items-center px-5 py-2.5 hover:bg-white/60 transition-colors" data-testid={`member-perf-${m.id}`}>
                        <div className="col-span-3 pl-8 flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                            {m.name.charAt(0)}
                          </div>
                          <span className="text-sm text-slate-700 truncate">{m.name}</span>
                          {m.on_leave && <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0 px-1">Leave</Badge>}
                        </div>
                        <div className="col-span-1 text-center text-sm font-medium text-slate-700">{m.total}</div>
                        <div className="col-span-1 text-center text-sm font-medium text-emerald-600">{m.completed}</div>
                        <div className="col-span-2 px-1">
                          <RateBar value={m.completion_rate} color="bg-emerald-500" bgColor="bg-emerald-100" />
                        </div>
                        <div className="col-span-2 px-1">
                          <RateBar value={m.delay_rate} color="bg-red-500" bgColor="bg-red-100" />
                        </div>
                        <div className="col-span-2 px-1">
                          <RateBar value={m.on_time_rate} color="bg-[#5B6FD6]" bgColor="bg-[#5B6FD6]/10" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DrillDownDialog({ drillDown, setDrillDown, getClientName, getUserName }) {
  if (!drillDown) return null;
  return (
    <Dialog open={!!drillDown} onOpenChange={() => setDrillDown(null)}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{drillDown.label} ({drillDown.tasks.length})</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          {drillDown.tasks.length === 0 ? (
            <p className="text-center py-8 text-slate-400">No tasks in this category</p>
          ) : (
            <table className="w-full text-sm" data-testid="drilldown-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2 pr-3">Task</th>
                  <th className="text-left py-2 pr-3">Client</th>
                  <th className="text-left py-2 pr-3">Assigned To</th>
                  <th className="text-left py-2 pr-3">Deadline</th>
                  <th className="text-left py-2 pr-3">Ageing</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {drillDown.tasks.map(task => (
                  <tr key={task.id} className="hover:bg-slate-50" data-testid={`drilldown-task-${task.id}`}>
                    <td className="py-2.5 pr-3">
                      <Link to={`/tasks/${task.id}`} className="font-medium text-slate-900 hover:text-[#5B6FD6] transition-colors" onClick={() => setDrillDown(null)}>
                        {task.title}
                      </Link>
                      <p className="text-xs text-slate-400 mt-0.5">{task.service_name}</p>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{getClientName(task.client_id)}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{getUserName(task.assignee_id)}</td>
                    <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                      {new Date(task.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-2.5 pr-3" data-testid={`drilldown-ageing-${task.id}`}>
                      {(() => {
                        const ref = task.updated_at || task.created_at;
                        if (!ref) return null;
                        const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                        return (
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${days > 15 ? 'bg-red-100 text-red-700' : days > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                            {days}d
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-2.5">
                      <Badge className={`text-xs text-white ${STATUS_COLORS[task.status] || 'bg-slate-500'}`}>
                        {STATUS_LABELS[task.status] || task.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoginStatusWidget() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchLoginStatus = async () => {
    try {
      const res = await api.get('/users/login-status');
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to fetch login status:', err);
    }
    finally { setLoading(false); }
  };

  // Refresh more frequently for live online status
  useEffect(() => {
    fetchLoginStatus();
    const i = setInterval(fetchLoginStatus, 20000); // every 20s for live feel
    return () => clearInterval(i);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const notLoggedIn = data.filter(u => !u.logged_in_today && !u.on_leave && !u.is_online);
  const stale = notLoggedIn.filter(u => (u.days_inactive ?? 9999) >= 1);
  const onlineNow = data.filter(u => u.is_online);
  const loggedInToday = data.filter(u => u.logged_in_today);
  // "Logged in earlier today but currently offline" — they have an entry in
  // last_login_at today but no active WebSocket right now (closed tab,
  // mobile screen lock, etc.). They're NOT absent, just away.
  const offlineToday = data.filter(u => u.logged_in_today && !u.is_online);

  const triggerCheck = async () => {
    setTriggering(true);
    try {
      await api.post('/admin/check-login-status-now');
      toast.success('Login check triggered. You\'ll receive a notification if any employee is absent.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger login check');
    } finally { setTriggering(false); }
  };

  if (loading) return null;

  return (
    <Card className="border-slate-200 shadow-sm mb-6" data-testid="login-status-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <User className="h-5 w-5 text-[#5B6FD6]" /> Live Login Status
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs ml-2 flex items-center gap-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              {onlineNow.length} online now
            </Badge>
            {offlineToday.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs" data-testid="logged-earlier-badge">
                {offlineToday.length} logged in earlier
              </Badge>
            )}
            {notLoggedIn.length > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                {notLoggedIn.length} absent
              </Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={triggerCheck} disabled={triggering}
            data-testid="trigger-login-check-btn" className="h-8 text-xs">
            {triggering ? 'Sending…' : 'Send Alert Now'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* ONLINE NOW */}
        {onlineNow.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" /> Online Now
            </p>
            <div className="space-y-1.5">
              {onlineNow.map(u => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 rounded bg-green-50/60 border border-green-100" data-testid={`online-${u.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`relative h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${u.role === 'admin' ? 'bg-gradient-to-br from-[#5B6FD6] to-[#4338CA]' : 'bg-gradient-to-br from-[#10B981] to-[#059669]'}`}>
                      {u.full_name.charAt(0).toUpperCase()}
                      <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white"></span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.role === 'admin' ? 'Admin' : 'Member'}</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500 text-white text-xs">Active</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LOGGED IN EARLIER TODAY — currently offline */}
        {offlineToday.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Logged In Earlier Today
              <span className="text-[10px] font-normal text-slate-400 normal-case tracking-normal">(currently offline — tab closed / mobile locked)</span>
            </p>
            <div className="space-y-1.5">
              {offlineToday.map(u => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 rounded bg-amber-50/40 border border-amber-100" data-testid={`offline-today-${u.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${u.role === 'admin' ? 'bg-gradient-to-br from-[#5B6FD6] to-[#4338CA]' : 'bg-gradient-to-br from-[#10B981] to-[#059669]'} opacity-75`}>
                      {u.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{u.full_name}</p>
                      <p className="text-xs text-slate-500">
                        {u.role === 'admin' ? 'Admin' : 'Member'}
                        {u.last_login_at && (() => {
                          try {
                            const d = new Date(u.last_login_at);
                            return ` · last seen ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                          } catch { return ''; }
                        })()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Away</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOT LOGGED IN TODAY */}
        {notLoggedIn.length === 0 ? (
          onlineNow.length === 0 && offlineToday.length === 0 && (
            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              No one has logged in today.
            </div>
          )
        ) : (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Not Logged In Today</p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {notLoggedIn.map(u => {
                const isStale = stale.includes(u);
                const days = u.days_inactive;
                const label = u.last_login_at === null || u.last_login_at === undefined
                  ? 'Never logged in'
                  : days === 0 ? 'Not today' : `${days} day${days > 1 ? 's' : ''} ago`;
                return (
                  <div key={u.id} className={`flex items-center justify-between px-3 py-2 rounded ${isStale ? 'bg-red-50/70' : 'bg-amber-50/60'} border border-slate-100`} data-testid={`absent-${u.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${u.role === 'admin' ? 'bg-gradient-to-br from-[#5B6FD6] to-[#4338CA]' : 'bg-gradient-to-br from-[#10B981] to-[#059669]'}`}>
                        {u.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-slate-500">{u.role === 'admin' ? 'Admin' : 'Member'} · {u.mobile_number}</p>
                      </div>
                    </div>
                    <Badge className={`text-xs ${isStale ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                      {label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          Refreshes every 20s · Auto-alert sent daily at 11 AM IST
        </p>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [drillDown, setDrillDown] = useState(null); // { label, tasks }
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const [leaveDate, setLeaveDate] = useState('');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showMyDates, setShowMyDates] = useState(false);
  const myUser = users.find(u => u.id === currentUser.id) || {};
  const missingMyDates = !myUser.date_of_birth && !myUser.joining_date;

  useEffect(() => { fetchData(); }, []);
  // Refresh on any WebSocket notification instead of polling every 10s.
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('ws-notification', handler);
    return () => window.removeEventListener('ws-notification', handler);
  }, []);
  useEffect(() => {
    if (currentUser.role === 'super_admin') return;
    // Once-per-day gate: this nudge should fire only on the first dashboard
    // view of the calendar day (per user) — NOT every time the user clicks
    // the Dashboard sidebar item. The flag is keyed by today's local date so
    // it resets automatically at midnight.
    const today = new Date().toISOString().slice(0, 10);
    const seenKey = `prev-day-prompt-${currentUser.id}-${today}`;
    if (localStorage.getItem(seenKey)) return;
    checkPreviousDay(seenKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time login nudge: if my DOB & joining date are both missing,
  // prompt me to fill them in via a non-blocking toast (once per browser session).
  useEffect(() => {
    try {
      if (!myUser.id) return;
      const nudgeKey = `dates-nudge-shown-${myUser.id}`;
      if (sessionStorage.getItem(nudgeKey)) return;
      if (myUser.date_of_birth || myUser.joining_date) return;
      sessionStorage.setItem(nudgeKey, '1');
      toast.info('Tell us your birthday so the team can wish you 🎂  — open the sidebar profile to add your dates.', { duration: 8000 });
    } catch (err) {
      console.warn('dates-nudge skipped:', err);
    }
  }, [myUser.id, myUser.date_of_birth, myUser.joining_date]);

  const checkPreviousDay = async (seenKey) => {
    try {
      const res = await api.get('/leave/check-previous');
      if (res.data.needs_action) {
        setLeaveDate(res.data.date);
        setShowLeavePrompt(true);
      }
      // Mark seen for today regardless of outcome — the user either:
      //  (a) had nothing pending → no point re-checking on every nav, or
      //  (b) saw the prompt → respecting their first-view-of-the-day rule
      // The flag resets automatically tomorrow because it's keyed by date.
      if (seenKey) localStorage.setItem(seenKey, '1');
    } catch (err) { console.warn('Previous-leave check failed:', err); }
  };

  const handleMarkLeave = async () => {
    try {
      await api.post(`/leave?date=${leaveDate}`);
      toast.success(`Leave marked for ${leaveDate}`);
      setShowLeavePrompt(false);
      setShowLeaveDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark leave'); }
  };

  const fetchData = async () => {
    try {
      const [tasksRes, clientsRes, usersRes, analyticsRes] = await Promise.all([
        api.get('/tasks'), api.get('/clients'), api.get('/users'),
        api.get('/analytics/dashboard')
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.company || '-';
  const getUserName = (id) => users.find(u => u.id === id)?.full_name || '-';
  const now = new Date();

  const getPriority = (task) => {
    if (task.status === 'completed') return 3;
    const deadline = new Date(task.deadline);
    if (deadline < now) return 0; // overdue - top
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (deadline < tomorrow) return 1; // due today
    return 2; // normal
  };

  const getPriorityColor = (task) => {
    const p = getPriority(task);
    if (p === 0) return 'border-l-4 border-l-red-500 bg-red-50/50';
    if (p === 1) return 'border-l-4 border-l-orange-400 bg-orange-50/30';
    return '';
  };

  const sortedTasks = [...tasks].filter(t => t.status !== 'completed').sort((a, b) => getPriority(a) - getPriority(b));
  const overdueTasks = tasks.filter(t => t.status !== 'completed' && new Date(t.deadline) < now);
  const dueTodayTasks = tasks.filter(t => {
    if (t.status === 'completed') return false;
    const d = new Date(t.deadline);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    return d >= today && d < tomorrow;
  });
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const pendingWithClient = tasks.filter(t => t.status === 'pending_with_client');
  const pendingWithMember = tasks.filter(t => t.status === 'pending_with_member');

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const onHoldTasks = tasks.filter(t => t.status === 'on_hold_client');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const pendingReviewTasks = tasks.filter(t => t.status === 'pending_review');

  const FILTER_MAP = {
    'all': { fn: () => tasks, label: 'All Tasks' },
    'all_active': { fn: () => tasks.filter(t => t.status !== 'completed'), label: 'All Active Tasks' },
    'due_today': { fn: () => dueTodayTasks, label: 'Due Today' },
    'in_progress': { fn: () => inProgressTasks, label: 'In Progress' },
    'overdue': { fn: () => overdueTasks, label: 'Overdue' },
    'pending_with_member': { fn: () => pendingWithMember, label: 'Pending with Member' },
    'pending_with_client': { fn: () => pendingWithClient, label: 'Pending with Client' },
    'pending': { fn: () => pendingTasks, label: 'Pending' },
    'on_hold': { fn: () => onHoldTasks, label: 'On Hold' },
    'pending_review': { fn: () => pendingReviewTasks, label: 'Pending Review' },
    'completed': { fn: () => completedTasks, label: 'Completed' },
  };

  const openDrillDown = (filterKey) => {
    const entry = FILTER_MAP[filterKey];
    if (entry) setDrillDown({ label: entry.label, tasks: entry.fn() });
  };

  const downloadDashboardXlsx = async (category, label) => {
    try {
      const res = await api.get(`/dashboard/tasks/export?category=${category}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `dashboard_${category}.xlsx`;
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  // MEMBER DASHBOARD - Activity-focused
  if (currentUser.role === 'member') {
    const memberCompleted = completedTasks.length;
    const totalHours = tasks.reduce((sum, t) => sum + (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0), 0);

    return (
      <div className="p-8 min-h-full bg-slate-50">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="member-greeting">Welcome, {currentUser.full_name?.split(' ')[0]}</h1>
              <p className="text-slate-500">Here's your task overview for today</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowMyDates(true)}
                className={`flex items-center gap-1.5 ${missingMyDates ? 'border-pink-300 text-pink-700 bg-pink-50 hover:bg-pink-100' : 'border-slate-200 text-slate-600'}`}
                data-testid="open-my-dates-btn">
                <Cake className="h-4 w-4" /> {missingMyDates ? 'Add my dates' : 'My dates'}
              </Button>
              <LogClientTimeButton clients={clients} onLogged={fetchData} />
            </div>
          </div>

          <CelebrationsBanner currentUserId={currentUser.id} />
          <UpcomingCelebrationsCard />
          <MyDaySummaryCard />

          <CrossTeamTasksCard />

          <DailyTipCard />

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
            {[
              { label: 'My Tasks', value: tasks.filter(t => t.status !== 'completed').length, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10', icon: CheckSquare, filterKey: 'all_active', exportCategory: 'active' },
              { label: 'Due Today', value: dueTodayTasks.length, color: 'text-orange-600', bg: 'bg-orange-50', icon: Clock, filterKey: 'due_today', exportCategory: 'due_today' },
              { label: 'In Progress', value: inProgressTasks.length, color: 'text-blue-600', bg: 'bg-blue-50', icon: Play, filterKey: 'in_progress' },
              { label: 'Overdue', value: overdueTasks.length, color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle, filterKey: 'overdue', exportCategory: 'overdue' },
              { label: 'Completed', value: memberCompleted, color: 'text-green-600', bg: 'bg-green-50', icon: CheckSquare, filterKey: 'completed' },
            ].map(c => (
              <Card key={c.label} className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow relative" onClick={() => openDrillDown(c.filterKey)} data-testid={`member-card-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                {c.exportCategory && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); downloadDashboardXlsx(c.exportCategory, c.label); }}
                    className="absolute top-1 right-1 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 z-10"
                    title={`Download ${c.label} as Excel`}
                    data-testid={`dl-${c.exportCategory}-btn`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
                  <div><p className="text-xs text-slate-500">{c.label}</p><p className="text-xl font-bold text-slate-900">{c.value}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
            {/* Status Breakdown */}
            <Card className="border-slate-200 shadow-sm" data-testid="member-status-breakdown">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">My Status Breakdown</CardTitle>
                  <span className="text-xs text-slate-400">Total: {tasks.length} | {totalHours.toFixed(1)}h logged</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {[
                    { label: 'Pending', count: pendingTasks.length, color: 'bg-slate-500', filterKey: 'pending' },
                    { label: 'In Progress', count: inProgressTasks.length, color: 'bg-blue-500', filterKey: 'in_progress' },
                    { label: 'Pending with Client', count: pendingWithClient.length, color: 'bg-orange-500', filterKey: 'pending_with_client' },
                    { label: 'Pending with Member', count: pendingWithMember.length, color: 'bg-purple-500', filterKey: 'pending_with_member' },
                    { label: 'Pending Review', count: pendingReviewTasks.length, color: 'bg-cyan-600', filterKey: 'pending_review' },
                    { label: 'On Hold', count: onHoldTasks.length, color: 'bg-amber-500', filterKey: 'on_hold' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => openDrillDown(s.filterKey)} data-testid={`member-status-${s.filterKey}`}>
                      <div className="flex items-center gap-2"><div className={`w-2.5 h-2.5 rounded-full ${s.color}`} /><span className="text-sm text-slate-700">{s.label}</span></div>
                      <div className="flex items-center gap-2"><span className="font-bold text-sm text-slate-900">{s.count}</span><ChevronRight className="h-3.5 w-3.5 text-slate-400" /></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Active Tasks */}
            <Card className="border-slate-200 shadow-sm col-span-2" data-testid="member-task-list">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Your Active Tasks</CardTitle>
                  <Link to="/tasks"><Button variant="outline" size="sm" data-testid="view-all-tasks">View All <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {sortedTasks.length === 0 ? (
                  <p className="text-center py-8 text-slate-400">No active tasks</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {sortedTasks.slice(0, 8).map(task => (
                      <Link key={task.id} to={`/tasks/${task.id}`} className={`flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors ${getPriorityColor(task)}`} data-testid={`member-task-${task.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900 truncate">{task.title}</span>
                            <Badge variant="outline" className="text-xs">{task.service_name}</Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>{getClientName(task.client_id)}</span>
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(task.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const ref = task.updated_at || task.created_at;
                            if (!ref) return null;
                            const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                            return (
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${days > 15 ? 'bg-red-100 text-red-700' : days > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`member-ageing-${task.id}`}>
                                {days}d
                              </span>
                            );
                          })()}
                          <Badge className={`text-xs text-white ${STATUS_COLORS[task.status] || 'bg-slate-500'}`}>{STATUS_LABELS[task.status] || task.status}</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <DrillDownDialog drillDown={drillDown} setDrillDown={setDrillDown} getClientName={getClientName} getUserName={getUserName} />

          <MyProfileDatesDialog
            open={showMyDates}
            onOpenChange={setShowMyDates}
            initialDOB={myUser.date_of_birth || ''}
            initialJoin={myUser.joining_date || ''}
            onSaved={fetchData}
          />

          {/* Action Required - Time Log / Leave Prompt */}
          <Dialog open={showLeavePrompt} onOpenChange={setShowLeavePrompt}>
            <DialogContent className="sm:max-w-md" data-testid="action-required-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Action Required</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                No time was logged for <span className="font-semibold text-slate-700">{leaveDate}</span>. Timer logging is mandatory. Please mark leave or log time before continuing.
              </p>
              <div className="flex gap-3 mt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowLeavePrompt(false)} data-testid="log-time-btn">I'll Log Time</Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => { setShowLeaveDialog(true); setShowLeavePrompt(false); }} data-testid="mark-leave-btn">Mark Leave</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
            <DialogContent className="sm:max-w-sm" data-testid="leave-confirm-dialog">
              <DialogHeader><DialogTitle>Confirm Leave</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">Mark <span className="font-semibold">{leaveDate}</span> as leave?</p>
              <div className="flex gap-3 mt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowLeaveDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={handleMarkLeave} data-testid="confirm-leave-btn">Confirm Leave</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
    );
  }

  // ADMIN DASHBOARD - Team management focused
  if (currentUser.role === 'admin') {
    // Only include the admin themselves + members reporting to this admin (parent_admin_id matches)
    const teamMembers = users.filter(u =>
      u.id === currentUser.id ||
      (u.role === 'member' && u.parent_admin_id === currentUser.id)
    );
    const teamWorkload = teamMembers.map(u => {
      const uTasks = tasks.filter(t => t.assignee_id === u.id);
      const activeTasks = uTasks.filter(t => t.status !== 'completed');
      const pending = activeTasks.filter(t => ['pending', 'pending_with_member'].includes(t.status));
      const overdue = activeTasks.filter(t => new Date(t.deadline) < now);
      const totalHours = uTasks.reduce((sum, t) => sum + (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0), 0);
      return { ...u, total: uTasks.length, active: activeTasks.length, pending: pending.length, overdue: overdue.length, hours: totalHours.toFixed(1) };
    }).sort((a, b) => b.active - a.active);

    return (
      <div className="p-8 min-h-full bg-slate-50">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="admin-greeting">Team Overview</h1>
              <p className="text-slate-500">Monitor your team's tasks and workload</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowMyDates(true)}
                className={`flex items-center gap-1.5 ${missingMyDates ? 'border-pink-300 text-pink-700 bg-pink-50 hover:bg-pink-100' : 'border-slate-200 text-slate-600'}`}
                data-testid="open-my-dates-btn">
                <Cake className="h-4 w-4" /> {missingMyDates ? 'Add my dates' : 'My dates'}
              </Button>
              <LogClientTimeButton clients={clients} onLogged={fetchData} />
            </div>
          </div>

          <CelebrationsBanner currentUserId={currentUser.id} />
          <UpcomingCelebrationsCard />
          <MyDaySummaryCard />

          <CrossTeamTasksCard />

          <DailyTipCard />

          {/* Excel export toolbar — admin */}
          <Card className="border-emerald-200 bg-emerald-50/50 mb-4" data-testid="admin-export-toolbar">
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Download reports:</span>
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" onClick={() => downloadDashboardXlsx('active', 'Active Tasks')} data-testid="dl-active-toolbar-btn">
                <Download className="h-4 w-4 mr-1.5" /> Active Tasks
              </Button>
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => downloadDashboardXlsx('overdue', 'Overdue Tasks')} data-testid="dl-overdue-toolbar-btn">
                <Download className="h-4 w-4 mr-1.5" /> Overdue
              </Button>
              <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => downloadDashboardXlsx('due_today', 'Due Today')} data-testid="dl-due_today-toolbar-btn">
                <Download className="h-4 w-4 mr-1.5" /> Due Today
              </Button>
            </CardContent>
          </Card>


          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
            {[
              { label: 'Total Tasks', value: tasks.length, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10', icon: CheckSquare, filterKey: 'all', exportCategory: 'active' },
              { label: 'Pending w/ Members', value: pendingWithMember.length, color: 'text-purple-600', bg: 'bg-purple-50', icon: Users, filterKey: 'pending_with_member' },
              { label: 'Pending w/ Client', value: pendingWithClient.length, color: 'text-orange-600', bg: 'bg-orange-50', icon: Clock, filterKey: 'pending_with_client' },
              { label: 'Overdue', value: overdueTasks.length, color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle, filterKey: 'overdue', exportCategory: 'overdue' },
              { label: 'Due Today', value: dueTodayTasks.length, color: 'text-amber-600', bg: 'bg-amber-50', icon: Calendar, filterKey: 'due_today', exportCategory: 'due_today' },
            ].map(c => (
              <Card key={c.label} className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow relative" onClick={() => openDrillDown(c.filterKey)} data-testid={`admin-card-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                {c.exportCategory && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); downloadDashboardXlsx(c.exportCategory, c.label); }} className="absolute top-1 right-1 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 z-10" title={`Download ${c.label} as Excel`} data-testid={`dl-${c.exportCategory}-btn`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
                  <div><p className="text-xs text-slate-500">{c.label}</p><p className="text-xl font-bold text-slate-900">{c.value}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Team Workload Table */}
            <Card className="border-slate-200 shadow-sm col-span-2" data-testid="admin-team-workload">
              <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-[#5B6FD6]" /> Team Workload</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="workload-table">
                    <thead>
                      <tr className="bg-slate-50 border-y border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        <th className="text-left px-5 py-3">Member</th>
                        <th className="text-center px-3 py-3">Tasks</th>
                        <th className="text-center px-3 py-3">Pending</th>
                        <th className="text-center px-3 py-3">Overdue</th>
                        <th className="text-center px-3 py-3">Hours</th>
                        <th className="text-center px-3 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {teamWorkload.map(m => (
                        <tr key={m.id} className={`hover:bg-slate-50 ${m.overdue > 3 ? 'bg-red-50/40' : ''}`} data-testid={`workload-${m.id}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{m.full_name?.charAt(0)}</div>
                              <div><p className="font-medium text-slate-900">{m.full_name}</p><p className="text-xs text-slate-500 capitalize">{m.role}</p></div>
                            </div>
                          </td>
                          <td className="text-center px-3 py-3 font-semibold text-slate-900">{m.active}</td>
                          <td className="text-center px-3 py-3"><Badge variant="outline" className="text-xs">{m.pending}</Badge></td>
                          <td className="text-center px-3 py-3">
                            {m.overdue > 0 ? <Badge className="bg-red-100 text-red-700 text-xs">{m.overdue}</Badge> : <span className="text-slate-400">0</span>}
                          </td>
                          <td className="text-center px-3 py-3 font-mono text-xs text-slate-600">{m.hours}h</td>
                          <td className="text-center px-3 py-3">
                            {m.overdue > 3 ? (
                              <Badge className="bg-red-500 text-white text-xs">Overloaded</Badge>
                            ) : m.active > 10 ? (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">High Load</Badge>
                            ) : (
                              <Badge className="bg-[#10B981]/10 text-[#10B981] text-xs">Normal</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Urgent Tasks */}
            <Card className="border-slate-200 shadow-sm" data-testid="admin-urgent-tasks">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" /> Urgent Tasks</CardTitle>
                  <Link to="/tasks"><Button variant="outline" size="sm">View All</Button></Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {[...overdueTasks, ...dueTodayTasks].slice(0, 8).map(task => (
                    <Link key={task.id} to={`/tasks/${task.id}`} className={`flex items-center justify-between px-5 py-3 hover:bg-slate-50 ${getPriorityColor(task)}`}>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-slate-900 truncate block">{task.title}</span>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                          <span>{getClientName(task.client_id)}</span>
                          <span>{getUserName(task.assignee_id)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const ref = task.updated_at || task.created_at;
                          if (!ref) return null;
                          const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                          return (
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${days > 15 ? 'bg-red-100 text-red-700' : days > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`admin-ageing-${task.id}`}>
                              {days}d
                            </span>
                          );
                        })()}
                        <Badge className={`text-xs text-white ${STATUS_COLORS[task.status]}`}>{STATUS_LABELS[task.status]}</Badge>
                      </div>
                    </Link>
                  ))}
                  {overdueTasks.length === 0 && dueTodayTasks.length === 0 && <p className="text-center py-6 text-slate-400 text-sm">No urgent tasks</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          <DrillDownDialog drillDown={drillDown} setDrillDown={setDrillDown} getClientName={getClientName} getUserName={getUserName} />

          <MyProfileDatesDialog
            open={showMyDates}
            onOpenChange={setShowMyDates}
            initialDOB={myUser.date_of_birth || ''}
            initialJoin={myUser.joining_date || ''}
            onSaved={fetchData}
          />

          {/* Action Required - Time Log / Leave Prompt */}
          <Dialog open={showLeavePrompt} onOpenChange={setShowLeavePrompt}>
            <DialogContent className="sm:max-w-md" data-testid="action-required-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Action Required</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                No time was logged for <span className="font-semibold text-slate-700">{leaveDate}</span>. Timer logging is mandatory. Please mark leave or log time before continuing.
              </p>
              <div className="flex gap-3 mt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowLeavePrompt(false)} data-testid="log-time-btn">I'll Log Time</Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => { setShowLeaveDialog(true); setShowLeavePrompt(false); }} data-testid="mark-leave-btn">Mark Leave</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
            <DialogContent className="sm:max-w-sm" data-testid="leave-confirm-dialog">
              <DialogHeader><DialogTitle>Confirm Leave</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">Mark <span className="font-semibold">{leaveDate}</span> as leave?</p>
              <div className="flex gap-3 mt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowLeaveDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={handleMarkLeave} data-testid="confirm-leave-btn">Confirm Leave</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
    );
  }

  // SUPER ADMIN DASHBOARD - Clean overview with clickable drill-downs
  const totalTasks = analytics?.total_tasks || 0;
  const activeTasks = totalTasks - (analytics?.completed_tasks || 0);

  const triggerDigest = async () => {
    try {
      await api.post('/admin/trigger-daily-digest');
      toast.success('Daily Digest sent to all admins');
    } catch { toast.error('Failed to trigger digest'); }
  };

  return (
    <div className="p-8 min-h-full bg-slate-50">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="superadmin-greeting">Welcome, {currentUser.full_name?.split(' ')[0]}</h1>
            <p className="text-slate-500">Click any card or status row for details</p>
          </div>
          <Button variant="outline" onClick={triggerDigest} data-testid="trigger-digest-btn"
            className="flex items-center gap-2 text-[#5B6FD6] border-[#5B6FD6]/30 hover:bg-[#5B6FD6]/5">
            <Send className="h-4 w-4" /> Send Daily Digest
          </Button>
        </div>

        <div className="flex justify-end mb-4 gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowMyDates(true)}
            className={`flex items-center gap-1.5 ${missingMyDates ? 'border-pink-300 text-pink-700 bg-pink-50 hover:bg-pink-100' : 'border-slate-200 text-slate-600'}`}
            data-testid="open-my-dates-btn">
            <Cake className="h-4 w-4" /> {missingMyDates ? 'Add my dates' : 'My dates'}
          </Button>
          <LogClientTimeButton clients={clients} onLogged={fetchData} />
        </div>

        <CelebrationsBanner currentUserId={currentUser.id} />
        <UpcomingCelebrationsCard />
        <MyDaySummaryCard />

        <CrossTeamTasksCard />

        <DailyTipCard />

        {/* Excel export toolbar — prominent so the export is discoverable */}
        <Card className="border-emerald-200 bg-emerald-50/50 mb-4" data-testid="sa-export-toolbar">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Download reports:</span>
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" onClick={() => downloadDashboardXlsx('active', 'Active Tasks')} data-testid="dl-active-toolbar-btn">
              <Download className="h-4 w-4 mr-1.5" /> Active Tasks
            </Button>
            <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => downloadDashboardXlsx('overdue', 'Overdue Tasks')} data-testid="dl-overdue-toolbar-btn">
              <Download className="h-4 w-4 mr-1.5" /> Overdue
            </Button>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => downloadDashboardXlsx('due_today', 'Due Today')} data-testid="dl-due_today-toolbar-btn">
              <Download className="h-4 w-4 mr-1.5" /> Due Today
            </Button>
            <span className="text-[11px] text-slate-500 ml-1">Or click the Excel button on any metric card below</span>
          </CardContent>
        </Card>

        {/* Key metrics - 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
          {[
            { label: 'Active Tasks', value: activeTasks, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10', icon: CheckSquare, filterKey: 'all_active', exportCategory: 'active' },
            { label: 'Overdue', value: overdueTasks.length, color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle, filterKey: 'overdue', exportCategory: 'overdue' },
            { label: 'Due Today', value: dueTodayTasks.length, color: 'text-amber-600', bg: 'bg-amber-50', icon: Calendar, filterKey: 'due_today', exportCategory: 'due_today' },
          ].map(c => (
            <Card key={c.label} className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow relative group" onClick={() => openDrillDown(c.filterKey)} data-testid={`sa-card-${c.label.toLowerCase().replace(/ /g, '-')}`}>
              {c.exportCategory && (
                <button type="button" onClick={(e) => { e.stopPropagation(); downloadDashboardXlsx(c.exportCategory, c.label); }} className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 text-[10px] font-semibold z-10" title={`Download ${c.label} as Excel`} data-testid={`dl-${c.exportCategory}-btn`}>
                  <Download className="h-3.5 w-3.5" /> Excel
                </button>
              )}
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-xl ${c.bg} flex items-center justify-center`}><c.icon className={`h-6 w-6 ${c.color}`} /></div>
                <div><p className="text-sm text-slate-500">{c.label}</p><p className="text-3xl font-bold text-slate-900">{c.value}</p></div>
                <ChevronRight className="h-5 w-5 text-slate-300 ml-auto" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Status Breakdown - full width, main focus */}
        <Card className="border-slate-200 shadow-sm mb-6" data-testid="sa-status-breakdown">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Task Status Breakdown</CardTitle>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>Total: <strong className="text-slate-900">{totalTasks}</strong></span>
                <span>Completed: <strong className="text-green-600">{analytics?.completed_tasks || 0}</strong></span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {[
                { label: 'In Progress', count: analytics?.in_progress_tasks || 0, color: 'bg-blue-500', filterKey: 'in_progress' },
                { label: 'Pending', count: analytics?.pending_tasks || 0, color: 'bg-slate-500', filterKey: 'pending' },
                { label: 'Pending with Client', count: analytics?.pending_with_client_tasks || 0, color: 'bg-orange-500', filterKey: 'pending_with_client' },
                { label: 'Pending with Member', count: analytics?.pending_with_member_tasks || 0, color: 'bg-purple-500', filterKey: 'pending_with_member' },
                { label: 'Pending Review', count: analytics?.pending_review_tasks || 0, color: 'bg-cyan-600', filterKey: 'pending_review' },
                { label: 'On Hold', count: analytics?.on_hold_client_tasks || 0, color: 'bg-amber-500', filterKey: 'on_hold' },
                { label: 'Overdue', count: overdueTasks.length, color: 'bg-red-600', filterKey: 'overdue' },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => openDrillDown(s.filterKey)} data-testid={`sa-status-${s.filterKey}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${s.color}`} />
                    <span className="text-sm text-slate-700">{s.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-slate-900 min-w-[2rem] text-right">{s.count}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Team Performance Tracking */}
        <TeamPerformanceChart />

        {/* Login Status — Super Admin only */}
        {currentUser.role === 'super_admin' && <LoginStatusWidget />}

        {/* Quick links - compact row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          {[
            { to: '/team-efficiency', label: 'Team Efficiency', icon: Users, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10', testId: 'sa-link-efficiency' },
            { to: '/timesheet', label: 'Timesheet Reports', icon: Clock, color: 'text-green-600', bg: 'bg-green-50', testId: 'sa-link-timesheet' },
            { to: '/activity-log', label: 'Activity Log', icon: CheckSquare, color: 'text-amber-600', bg: 'bg-amber-50', testId: 'sa-link-activity' },
          ].map(link => (
            <Link key={link.to} to={link.to} data-testid={link.testId}>
              <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-lg ${link.bg} flex items-center justify-center`}><link.icon className={`h-4 w-4 ${link.color}`} /></div>
                    <span className="font-medium text-sm text-slate-900">{link.label}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <DrillDownDialog drillDown={drillDown} setDrillDown={setDrillDown} getClientName={getClientName} getUserName={getUserName} />

        <MyProfileDatesDialog
          open={showMyDates}
          onOpenChange={setShowMyDates}
          initialDOB={myUser.date_of_birth || ''}
          initialJoin={myUser.joining_date || ''}
          onSaved={fetchData}
        />
    </div>
  );
}
